import CTNFilterBar, {
	CTNFilterButton,
	CTNFilterDropdown,
	CTNFilterSearch,
	CTNFilterInputSwitch,
} from '@ctn/ui/components/ctn-filter-bar';
import CTNTable from '@ctn/ui/components/ctn-table';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableComponentRowRef } from '@ctn/ui/types/ctn-table';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type IUsuariosListadoPayload,
	type IUsuariosListadoResponse,
	postUsuariosListado,
} from '@/services/usuarios.service';

import { PAGE_SIZE } from '@/constants/table.constants';
import { type IUserListado } from '@/interfaces/model/user.interface';

import getColumns from './columns';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_DEFAULT, PAYLOAD_PAGINATION } from './config';

type UsuariosListadoProps = {
	readonly onEdit: (rowData: IUserListado) => void;
};

type Pagination = Pick<IUsuariosListadoPayload, 'limit' | 'start'>;

const UsuariosListado = ({ onEdit }: UsuariosListadoProps): ReactElement => {
	const filterBarRef = useRef<CTNFilterBarRef>(null);
	const location = useLocation();

	const tableRef = useRef<CTNTableComponentRowRef<IUserListado>>(null);
	const { loading, triggerFetch } = useApiData({ loading: ['listado'] as const });
	const [dataTable, setDataTable] = useState<IUsuariosListadoResponse>({ totalResults: 10, listado: [] });
	const [pagination, setPagination] = useState<Pagination>(PAYLOAD_PAGINATION);
	const { navigateData, setNavigateData } = usePageContext();
	const {
		maestrosGeneric: { rol },
	} = useGlobalContext();

	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues: { ...defaultValues() },
	});

	const search = watch('search');
	const tipoBusqueda = search?.[0]?.key ?? defaultValues().tipoBusqueda;
	const listaIdRol = watch('listaIdRol');
	const activo = watch('activo');
	const [isReady, setIsReady] = useState(false);
	const isInitialLoad = useRef(true);
	const lastCallTimestamp = useRef<number>(0);

	const getData = async (payload: Pagination & Partial<IUsuariosListadoPayload>) => {
		const NOW = Date.now();
		const COOLDOWN_MS = 300; 

		if (NOW - lastCallTimestamp.current < COOLDOWN_MS) {
			console.warn(`Llamada bloqueada para evitar duplicados. Espera ${COOLDOWN_MS / 1000}s.`);
			return; 
		}

		lastCallTimestamp.current = NOW;

		const { data } = await triggerFetch({
			fetch: postUsuariosListado,
			payload: { ...PAYLOAD_DEFAULT, ...payload },
			name: 'listado',
		});
    
		if (data) {
			setDataTable(data);
		}
	};

	useEffect(() => {
		if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			getData(mapPayload(pagination, {  tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdRol, activo }));
			setIsReady(true);
			return;
		}
		const {data} = navigateData.filter;
		if (!data) {
			getData(mapPayload(pagination, {  tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdRol, activo }));
			setIsReady(true);
			return;
		}
		reset({
			...defaultValues(),
			search: [{ key: data.tipoBusqueda ?? defaultValues().tipoBusqueda, search: data.nombre }],
			tipoBusqueda: data.tipoBusqueda,
			busqueda: data.nombre,
			listaIdRol: data.listaIdRol?.map((id: number) => rol?.find((r) => r.id === id)?.descripcion ?? String(id)),
			activo: data.activo ? ['true'] : [],
		});
		filterBarRef.current?.addFilters({
			search: data.nombre ? [{ key: data.tipoBusqueda ?? defaultValues().tipoBusqueda, search: data.nombre ?? '' }] : [],
			listaIdRol: data.listaIdRol ?? [],
			activo: data.activo ? ['true'] : [],
		});
		const { filter, ...resto } = navigateData ?? {};
		setNavigateData(resto);
		setIsReady(true);
		isInitialLoad.current = false;
	}, []); 

	const mapPayload = (pagination: Pagination, filter: FilterFormData): IUsuariosListadoPayload => ({
		...pagination,
		[tipoBusqueda]: filter.busqueda ? filter.busqueda : undefined,
		listaIdRol: filter?.listaIdRol && filter.listaIdRol?.length > 0 ? filter.listaIdRol.map(Number) : undefined,
		activo: filter?.activo?.includes('true') ? true : undefined,
	});

	const onPageChange = (start: number) => {
		let newPagination = {
			start: PAYLOAD_PAGINATION.limit * (start - 1),
			limit: PAYLOAD_PAGINATION.limit,
		};

		if (start === 1) {
			newPagination = {
				start: 0,
				limit: PAYLOAD_PAGINATION.limit,
			};
		}
		getData(mapPayload(newPagination, {  tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdRol }));
		setPagination(newPagination);
	};

	useEffect(() => {
		if (!isReady) return;
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		const payload = mapPayload(pagination, { tipoBusqueda: search?.[0]?.key ?? '', busqueda: search?.[0]?.search ?? '', listaIdRol, activo });
		getData(payload);
		setNavigateData({ filter: { urls: [location.pathname], data: payload }});
	}, [search, JSON.stringify(listaIdRol), JSON.stringify(activo), isReady, isInitialLoad.current]);

	return (
		<section>
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar c-pnp-filter-bar--wrap"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" placeholder="Buscar por nombre" label="Buscar"
					tag="Búsqueda" 
					options={[{ label: 'Nombre', value: 'nombre' }]}/>
				<CTNFilterDropdown
					multiple
					selectAll
					showArrow={false}
					label="Tipo"
					valueFormatter={() => 'Tipo'}
					placeholder="Tipo"
					name="listaIdRol"
					tag="Tipo"
					options={rol?.map((value) => ({
						label: value.descripcion,
						value: String(value.id),
					}))}
				/>
				<CTNFilterInputSwitch
					noTag
					name="activo"
					tag="Ver sólo activo"
					items={[{ label: 'Ver sólo activo', value: 'true' }]}
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
				<div className="c-pnp-box__body">
					<CTNTable
						ref={tableRef}
						manualPagination
						idData="id"
						loading={loading.listado}
						loadingConf={{ spinner: true }}
						className="c-pnp-table"
						columns={getColumns({ onEdit })}
						data={dataTable.listado}
						totalData={dataTable.totalResults}
						noDataConf={{ description: 'Aún no has añadido ningún usuario' }}
						manualPageCount={dataTable.totalResults / PAGE_SIZE.USER}
						pageSize={PAGE_SIZE.USER}
						onPageChange={onPageChange}
					/>
				</div>
			</div>
		</section>
	);
};

export default UsuariosListado;
